import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { includes, upperCase, without } from 'lodash'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { useDebounce } from '@/hooks/useDebounce'

import { WorkflowSource } from '../constants'
import { useListCompanies } from '../hooks'

import { CompanyListSkeleton } from './CompanyListSkeleton'
import { CompanySearch } from './CompanySearch'

interface SelectCompanyModalProps {
  open: boolean
  onClose: () => void
  onAdd: (companyIds: string[]) => void
}

export const SelectCompanyModal: React.FC<SelectCompanyModalProps> = ({
  open,
  onClose,
  onAdd,
}) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery)
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([])

  const loadMoreRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const {
    data: companiesData,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useListCompanies(WorkflowSource.NonConfigured, debouncedSearchQuery, open)

  const companies = useMemo(() => {
    if (!companiesData) return []
    return companiesData.pages.flatMap((page) => page.data)
  }, [companiesData])

  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      setSelectedCompanyIds([])
    }
  }, [open])

  const handleToggleCompany = (companyId: string) => {
    setSelectedCompanyIds((prev) =>
      includes(prev, companyId)
        ? without(prev, companyId)
        : [...prev, companyId]
    )
  }

  const handleAdd = () => {
    if (selectedCompanyIds.length > 0) {
      onAdd(selectedCompanyIds)
      onClose()
    }
  }

  // Handle intersection observer for infinite scroll
  const stateRef = useRef({
    hasNextPage,
    isFetchingNextPage,
  })

  // Update ref without triggering re-renders
  useEffect(() => {
    stateRef.current = {
      hasNextPage,
      isFetchingNextPage,
    }
  }, [hasNextPage, isFetchingNextPage])

  // Stable callback that uses ref for latest values
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0]
      const { hasNextPage, isFetchingNextPage } = stateRef.current

      if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage]
  )

  // Set up IntersectionObserver - re-run when hasNextPage changes to ensure sentinel is observed
  useEffect(() => {
    const targetElement = loadMoreRef.current
    const rootElement = scrollContainerRef.current

    if (!targetElement || !hasNextPage) return

    const observer = new IntersectionObserver(handleIntersection, {
      root: rootElement || null,
      rootMargin: '100px',
      threshold: 0.1,
    })

    observer.observe(targetElement)

    return () => {
      observer.disconnect()
    }
  }, [handleIntersection, hasNextPage])

  const getInitials = (name: string | undefined | null): string => {
    if (!name) return ''
    return upperCase(name.slice(0, 2))
  }

  const renderCompanyList = () => {
    if (isLoading) {
      return <CompanyListSkeleton count={4} />
    }

    if (companies.length === 0) {
      return (
        <div className="py-4 text-sm text-center text-gray-light-600">
          {t('workflowConfig.noCompaniesFound')}
        </div>
      )
    }

    return (
      <>
        {companies.map((company) => (
          <Checkbox
            key={company.company_id}
            checked={includes(selectedCompanyIds, company.company_id)}
            onChange={() => handleToggleCompany(company.company_id)}
            checkBoxClassName="w-5 h-5"
            className="flex items-center w-full gap-3 p-3 transition-colors bg-white border rounded-lg border-gray-light-200 hover:bg-gray-50"
            label={
              <div className="flex items-center gap-3">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="text-sm font-semibold text-white bg-blue-500">
                    {getInitials(company.company_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-base font-normal text-gray-light-900">
                  {company.company_name}
                </span>
              </div>
            }
          />
        ))}
        {hasNextPage && <div ref={loadMoreRef} className="w-full h-5" />}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-2">
            <Spinner className="size-4 text-brand-600" />
          </div>
        )}
      </>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="pt-1 m-0">
            {t('workflowConfig.addReminder.title')}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-light-600">
            {t('workflowConfig.addReminder.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <CompanySearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('workflowConfig.searchCompany')}
          />

          <div ref={scrollContainerRef} className="overflow-y-auto max-h-96">
            {renderCompanyList()}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            variant="outlined"
            onClick={onClose}
            className="flex-1 text-secondary-700"
            fullWidth
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAdd}
            disabled={selectedCompanyIds.length === 0}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            fullWidth
          >
            {t('common.add')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
